// Customer Statements data layer (Phase 4, P4-CC8) — READ-ONLY aggregation.
//
// A statement is a VIEW of existing data — no tables, no writes. It reuses
// invoice_ledger for per-invoice amount_outstanding (aging + current total) and
// NEVER re-derives the payment/credit netting; the running balance is built from
// the raw dated records (invoices / invoice_payments / credit_notes).
//
// RECONCILIATION (asserted by the harness): when `to` ≥ today (or is null),
//   closing_balance === current_outstanding === Σ aging buckets.
// CREDIT BALANCE: an overpaid / over-credited customer nets to a negative balance;
// this is surfaced via `in_credit: true` (and negatives flow through aging so the
// reconciliation still holds) — it is not an error.

import { supabase } from "./supabaseClient";
import { getCustomer, listCustomers } from "./customers";   // read-only imports
// P4-CC19b — THIS IMPORT IS THE MECHANISM, not a convenience. Every receivables
// figure on the Statements screen and every receivables figure on the Dashboard
// now come from ONE server-side function (public.invoice_stats), reached through
// this one entry point. They disagreed by 1.48 precisely because each screen
// computed its own total from its own status filter; giving each a private RPC
// would have reproduced that disagreement in SQL, where it is harder to see.
// Do NOT replace this with a second aggregate call. See the migration header
// (20260811120000_cc19b_aggregates.sql, "THE 1.48 CONSTRAINT").
import { getInvoiceStats } from "./invoices";
import { todayISO, daysBetweenISO } from "./dates";

const LEDGER = "invoice_ledger";
const PAYMENTS = "invoice_payments";
const CREDITS = "credit_notes";
const MAX_ROWS = 9999;   // ≤10k cap for v1 (see getStatementCustomers note)

// Issued (real receivable) invoices in the derived ledger — excludes draft/cancelled.
const ISSUED = ["sent", "partial", "paid", "credited"];

export const CURRENCY = "EUR";

function client() {
  if (!supabase) throw new Error("Supabase is not configured (check .env).");
  return supabase;
}
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function toError(error, fallback) {
  const code = error && error.code;
  let message;
  switch (code) {
    case "42501": message = "You do not have access to this data."; break;
    case "PGRST116": message = "Customer not found, or you do not have access to it."; break;
    default: message = (error && error.message) || fallback;
  }
  const e = new Error(message); e.code = code; e.cause = error;
  return e;
}

// P4-CC19a-2 — getCustomerStatement is the one EXTERNALLY-visible number in this
// file (the document a customer receives, printable/downloadable in
// StatementView.jsx). COUNT-BASED truncation detection, NOT the "request one row
// past the cap" pattern this replaces: that pattern is structurally unfireable on
// this project, because PostgREST's server-side max_rows (confirmed live: Project
// Settings -> Data API -> Max Rows = 1000) caps EVERY response at a fixed ceiling
// regardless of what range/limit a client requests — so a fetch asking for
// MAX_ROWS+1 rows could never actually receive more than 1000, and the "did we
// get one extra row back" check could never observe the truncation it existed to
// catch. { count: "exact" } instead runs an independent, uncapped COUNT(*) against
// the same filter; comparing it to the returned row count is what actually
// detects the gap. A wrong total under-bills a customer who has no reason to
// notice or correct it — it may fail loudly, it must never be quietly wrong.
// getStatementCustomers (below) is UNCHANGED — its own capped fetch has no guard
// at all yet; that is Group B/CC19b, not this package.
function assertRowsComplete(count, dataLength, what) {
  // count == null is treated as untrustworthy, not as "0 rows exist" — a missing
  // count on a { count: "exact" } request is itself a signal something is wrong,
  // and defaulting it to 0 would silently disable this exact guard the same way
  // the pattern it replaced was silently disabled.
  if (count == null || count > dataLength) {
    const e = new Error(`This customer has too many ${what} to build a statement automatically. Contact support before sending this statement.`);
    e.code = "TOO_MANY_ROWS";
    throw e;
  }
}

/** Derive a clean statement header (primary contact email + default address) from a customer. */
function statementCustomer(cu) {
  const primary = (cu.customer_contacts || []).find((p) => p.is_primary) || (cu.customer_contacts || [])[0] || {};
  const a = (cu.customer_addresses || []).find((x) => x.is_default) || (cu.customer_addresses || [])[0] || {};
  const address = [a.line1, a.line2, [a.city, a.county].filter(Boolean).join(", "), a.postal_code, a.country].filter(Boolean).join("\n");
  return {
    id: cu.id, company_name: cu.company_name || null, customer_number: cu.customer_number || null,
    email: primary.email || null, vat_number: cu.vat_number || null,
    payment_terms: cu.payment_terms || null, address: address || null,
  };
}

/**
 * Landing list: active customers each annotated with their CURRENT total outstanding
 * (Σ invoice_ledger.amount_outstanding), plus the overall receivables total for the KPI.
 * NOTE: pulls issued ledger rows and groups in JS (≤10k cap). A DB-side receivables
 * aggregate (RPC / materialised view) is the future enhancement if invoice counts grow —
 * not built now.
 * @returns {Promise<{data:Array<{id,company_name,customer_number,outstanding}>, totalOutstanding:number, count:number}>}
 */
export async function getStatementCustomers() {
  const c = client();
  const [customers, ledgerRes, stats] = await Promise.all([
    listCustomers({ status: "active" }),
    c.from(LEDGER).select("customer_id, amount_outstanding, status, due_date").in("status", ISSUED).range(0, MAX_ROWS),
    getInvoiceStats(),
  ]);
  if (ledgerRes.error) throw toError(ledgerRes.error, "Could not load receivables");
  const byCustomer = new Map();
  for (const r of ledgerRes.data || []) {
    if (!r.customer_id) continue;
    byCustomer.set(r.customer_id, (byCustomer.get(r.customer_id) || 0) + (Number(r.amount_outstanding) || 0));
  }
  const rows = (customers || []).map((cu) => ({
    id: cu.id, company_name: cu.company_name, customer_number: cu.customer_number,
    outstanding: round2(byCustomer.get(cu.id) || 0),
  }));
  // P4-CC19b — THE KPIs ARE NOW SERVER-COMPUTED over every row, and come from
  // the same public.invoice_stats call the Dashboard reads (see the import
  // comment at the top of this file). They no longer depend on the capped fetch
  // above, so they are correct for a company of any size.
  //
  // `rows` DELIBERATELY STILL COMES FROM THE CAPPED FETCH, and is a PAGE, not
  // the population. Both the customer list and the ledger fetch above stop at
  // PostgREST's 1,000-row ceiling, so a listed customer's `outstanding` can be
  // understated for a very large tenant. That is CC19b-B2's job, not this
  // package's: CC19b-0 proved max_rows applies to set-returning RPCs too, so a
  // per-customer function would have moved the cliff rather than removed it, and
  // the right shape is a KPI over the whole population above a paginated table.
  // The KPI half is what shipped here; do not read totals off `rows`.
  return {
    data: rows,
    totalOutstanding: stats.netReceivables,
    overdueOutstanding: stats.overdueReceivables,
    customersWithBalance: stats.customersWithBalance,
    count: stats.activeCustomerCount,
  };
}

/**
 * TEMPORARY (P4-CC19b Phase 1) — the pre-CC19b KPI computation, kept ONLY so
 * supabase/verify/cc19b_aggregates.verify.mjs can diff it field-by-field against
 * the server-side version above on identical data. Carries the silent 1,000-row
 * truncation this package exists to remove, so nothing in src/ may call it.
 * DELETE once the parity harness has been run and accepted.
 */
export async function getStatementCustomersLegacy() {
  const c = client();
  const [customers, ledgerRes] = await Promise.all([
    listCustomers({ status: "active" }),
    c.from(LEDGER).select("customer_id, amount_outstanding, status, due_date").in("status", ISSUED).range(0, MAX_ROWS),
  ]);
  if (ledgerRes.error) throw toError(ledgerRes.error, "Could not load receivables");
  const t0 = todayISO();
  const byCustomer = new Map();
  let overdueOutstanding = 0;
  for (const r of ledgerRes.data || []) {
    const amt = Number(r.amount_outstanding) || 0;
    if (r.customer_id) byCustomer.set(r.customer_id, (byCustomer.get(r.customer_id) || 0) + amt);
    if (amt > 0 && r.due_date && r.due_date < t0) overdueOutstanding += amt;
  }
  const rows = (customers || []).map((cu) => ({
    id: cu.id, company_name: cu.company_name, customer_number: cu.customer_number,
    outstanding: round2(byCustomer.get(cu.id) || 0),
  }));
  const totalOutstanding = round2([...byCustomer.values()].reduce((s, v) => s + v, 0));
  const customersWithBalance = rows.filter((r) => r.outstanding > 0).length;
  return { data: rows, totalOutstanding, overdueOutstanding: round2(overdueOutstanding), customersWithBalance, count: rows.length };
}

/**
 * Build a customer statement over [from, to]: opening balance, a dated transaction
 * stream (invoices +, payments −, credits −) with a running balance, closing balance,
 * current total outstanding (from the ledger), and an aging summary.
 * @param {string} customerId
 * @param {{from?:string, to?:string}} [opts] ISO dates (both optional)
 * @returns {Promise<Object|null>} the statement shape, or null if the customer is not found.
 */
export async function getCustomerStatement(customerId, opts = {}) {
  const { from, to } = opts;
  const c = client();
  const customer = await getCustomer(customerId);
  if (!customer) return null;

  // (2) issued invoices — ledger gives total_amount (for the stream) + amount_outstanding (for aging/current).
  // { count: "exact" }, no .range() — PostgREST's own max_rows already bounds
  // `data`; count tells us the true total so truncation is detected rather than
  // silently under-reported on their statement (assertRowsComplete, below).
  const { data: invRows, error: invErr, count: invCount } = await c.from(LEDGER)
    .select("id, invoice_number, issue_date, due_date, total_amount, amount_outstanding, status", { count: "exact" })
    .eq("customer_id", customerId).in("status", ISSUED);
  if (invErr) throw toError(invErr, "Could not load the customer's invoices");
  assertRowsComplete(invCount, (invRows || []).length, "invoices");
  const invoices = invRows || [];
  const invoiceIds = invoices.map((i) => i.id);

  // (3) payments + (4) credit notes, matched by invoice_id (both always carry it).
  let payments = [], credits = [];
  if (invoiceIds.length) {
    const [payRes, crRes] = await Promise.all([
      c.from(PAYMENTS).select("invoice_id, payment_number, amount, payment_date, created_at", { count: "exact" }).in("invoice_id", invoiceIds).eq("voided", false),
      c.from(CREDITS).select("invoice_id, credit_note_number, issue_date, total_amount, created_at", { count: "exact" }).in("invoice_id", invoiceIds).eq("status", "issued"),
    ]);
    if (payRes.error) throw toError(payRes.error, "Could not load payments");
    if (crRes.error) throw toError(crRes.error, "Could not load credit notes");
    assertRowsComplete(payRes.count, (payRes.data || []).length, "payments");
    assertRowsComplete(crRes.count, (crRes.data || []).length, "credit notes");
    payments = payRes.data || []; credits = crRes.data || [];
  }

  // One dated stream (signed: invoice +total, payment −amount, credit −total).
  const stream = [
    ...invoices.map((i) => ({ date: i.issue_date, created_at: i.issue_date, type: "invoice", ref: i.invoice_number, description: "Invoice", debit: round2(i.total_amount), credit: 0, signed: round2(i.total_amount) })),
    ...payments.map((p) => ({ date: p.payment_date, created_at: p.created_at, type: "payment", ref: p.payment_number, description: "Payment received", debit: 0, credit: round2(p.amount), signed: -round2(p.amount) })),
    ...credits.map((cn) => ({ date: cn.issue_date, created_at: cn.created_at, type: "credit_note", ref: cn.credit_note_number, description: "Credit note", debit: 0, credit: round2(cn.total_amount), signed: -round2(cn.total_amount) })),
  ].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : (String(a.created_at) < String(b.created_at) ? -1 : 1)));

  // Opening = net of everything strictly before `from` (0 if no `from`).
  const opening_balance = from ? round2(stream.filter((t) => t.date < from).reduce((s, t) => s + t.signed, 0)) : 0;

  // Period [from, to] with a running balance seeded from the opening balance.
  const inPeriod = stream.filter((t) => (!from || t.date >= from) && (!to || t.date <= to));
  let run = opening_balance;
  const transactions = inPeriod.map((t) => {
    run = round2(run + t.signed);
    return { date: t.date, type: t.type, ref: t.ref, description: t.description, debit: t.debit, credit: t.credit, running_balance: run };
  });
  const closing_balance = round2(run);

  // Current total outstanding (as-of-now) straight from the ledger — never re-derived.
  const current_outstanding = round2(invoices.reduce((s, i) => s + (Number(i.amount_outstanding) || 0), 0));

  // Aging: bucket each issued invoice's amount_outstanding by days past its due date.
  const t0 = todayISO();
  const aging = { current: 0, d1_30: 0, d31_60: 0, d61_90: 0, d90_plus: 0, total: 0 };
  for (const i of invoices) {
    const amt = Number(i.amount_outstanding) || 0;
    if (amt === 0) continue;                                 // settled/credited invoices don't age
    const overdue = i.due_date ? daysBetweenISO(i.due_date, t0) : 0;   // negative if not yet due
    if (overdue <= 0) aging.current += amt;                  // not yet due, no due date, or in credit → current
    else if (overdue <= 30) aging.d1_30 += amt;
    else if (overdue <= 60) aging.d31_60 += amt;
    else if (overdue <= 90) aging.d61_90 += amt;
    else aging.d90_plus += amt;
  }
  aging.current = round2(aging.current); aging.d1_30 = round2(aging.d1_30); aging.d31_60 = round2(aging.d31_60);
  aging.d61_90 = round2(aging.d61_90); aging.d90_plus = round2(aging.d90_plus);
  aging.total = round2(aging.current + aging.d1_30 + aging.d31_60 + aging.d61_90 + aging.d90_plus);

  return {
    customer: statementCustomer(customer),
    period: { from: from || null, to: to || null },
    opening_balance,
    transactions,
    closing_balance,
    current_outstanding,
    in_credit: current_outstanding < 0,   // overpaid / over-credited → "in credit", not an error
    aging,
    currency: CURRENCY,
  };
}
